import React from 'react';
import classNames from 'classnames/bind';
import { Button, Combobox, Kbd, TextInput, useCombobox } from '@mantine/core';
import { matchSorter } from 'match-sorter';

import { parseModelLabels } from 'services/model_labels';
import { isNotNullOrUndefined } from 'services/nullable';
import { useActivePipeline } from 'pipelines/context/active_pipeline';
import { useCurrentNode } from 'deployments/hooks/useCurrentNode';
import { useMarketplaceModels, useModels } from 'hooks/api/useModels';
import { useReadFilesFromURL } from 'hooks/useReadFilesFromURL';

import { Text } from 'components/Text/Text';

import styles from './UpstreamModelLabels.module.scss';

const c = classNames.bind(styles);

type UpstreamModelLabelsInputProps = {
  id: string;
  values: string[];
  onSubmitValue: (value: string) => void;
};

export function UpstreamModelLabelsInput({
  id,
  values,
  onSubmitValue,
}: UpstreamModelLabelsInputProps) {
  const combobox = useCombobox();

  const [value, setValue] = React.useState('');

  const { nodeGraph } = useActivePipeline();
  const { nodeID } = useCurrentNode();

  const { data: userModels = [] } = useModels();
  const { data: marketplaceModels = [] } = useMarketplaceModels();

  const appModels = [...userModels, ...marketplaceModels];

  const upstreamNodes = nodeGraph.get(nodeID)?.allUpstreamNodes() || [];
  const modelNodes = [...upstreamNodes].filter(({ data }) => data.model_id);

  const upstreamModels = appModels.filter(({ id }) =>
    modelNodes.find(({ data }) => id === data.model_id)
  );

  // Detector models: Labels file contains model-labels
  const upstreamDetectorModels = upstreamModels.filter(
    (model) => model.capability === 'detection'
  );
  const detectorFileURLs = upstreamDetectorModels
    .map(({ labels_file_url }) => labels_file_url)
    .filter(isNotNullOrUndefined);
  const detectorFileResults = useReadFilesFromURL(detectorFileURLs);
  const labels = detectorFileResults
    .map(({ data }) => data)
    .filter(isNotNullOrUndefined)
    .flatMap(parseModelLabels);

  // Classifier models: Labels file contains attributes
  const upstreamClassifierModels = upstreamModels.filter(
    (model) => model.capability === 'classification'
  );
  const classifierFileURLs = upstreamClassifierModels
    .map(({ labels_file_url }) => labels_file_url)
    .filter(isNotNullOrUndefined);
  const classifierFileResults = useReadFilesFromURL(classifierFileURLs);
  const attributes = classifierFileResults
    .map(({ data }) => data)
    .filter(isNotNullOrUndefined)
    .flatMap(parseModelLabels);

  // Other models: Labels file values may be used as model-label or attribute
  const otherModels = upstreamModels.filter(
    (model) => model.capability === 'other'
  );
  const otherFileURLs = otherModels
    .map(({ labels_file_url }) => labels_file_url)
    .filter(isNotNullOrUndefined);
  const otherFileResults = useReadFilesFromURL(otherFileURLs);
  const labelsOrAttributes = otherFileResults
    .map(({ data }) => data)
    .filter(isNotNullOrUndefined)
    .flatMap(parseModelLabels);

  const labelsOptions = Array.from(
    new Set([...labels, ...labelsOrAttributes])
  ).sort((a, b) => a.localeCompare(b));
  const attributesOptions = [
    '*',
    ...Array.from(new Set([...attributes, ...labelsOrAttributes])).sort(
      (a, b) => a.localeCompare(b)
    ),
  ];

  const [cursorPosition, setCursorPosition] = React.useState<number | null>(
    null
  );

  const getEditingInfo = (text: string, cursor: number | null) => {
    if (!text.includes('.')) {
      return {
        isEditingLabel: true,
        activeSubstring: text,
        partIndex: 0,
        partStart: 0,
        partEnd: text.length,
      };
    }

    const dotPositions = [];
    for (let i = 0; i < text.length; i++) {
      if (text[i] === '.') {
        dotPositions.push(i);
      }
    }

    if (cursor === null) {
      const lastDotIndex = dotPositions[dotPositions.length - 1];
      return {
        isEditingLabel: false,
        activeSubstring: text.slice(lastDotIndex + 1),
        partIndex: dotPositions.length,
        partStart: lastDotIndex + 1,
        partEnd: text.length,
      };
    }

    let partIndex = 0;
    let partStart = 0;
    let partEnd = text.length;

    for (let i = 0; i < dotPositions.length; i++) {
      if (cursor <= dotPositions[i]) {
        partEnd = dotPositions[i];
        break;
      }
      partIndex = i + 1;
      partStart = dotPositions[i] + 1;
    }

    const activeSubstring = text.slice(partStart, partEnd);
    const isEditingLabel = partIndex === 0;

    return {
      isEditingLabel,
      activeSubstring,
      partIndex,
      partStart,
      partEnd,
    };
  };

  const { isEditingLabel, activeSubstring, partStart, partEnd } =
    getEditingInfo(value, cursorPosition);

  const activeSuggestions = isEditingLabel ? labelsOptions : attributesOptions;

  const filteredSuggestions = matchSorter(
    activeSuggestions.filter((suggestion) => !values.includes(suggestion)),
    activeSubstring,
    {
      keys: [(item) => item],
    }
  );

  const trimmedValue = value.trim();
  const canConfirm = trimmedValue.length > 0 && !trimmedValue.endsWith('.');
  const isOnlyLabel = trimmedValue.length > 0 && !value.includes('.');
  const isDuplicateValue =
    trimmedValue.length > 0 && values.includes(trimmedValue);

  const ADD_VALUE_PREFIX = '__add__:'; // internal sentinel for "Add <full value>"
  const canShowAddOption =
    canConfirm &&
    !isDuplicateValue &&
    !filteredSuggestions.some(
      (suggestion) => suggestion === activeSubstring.trim()
    );

  function commitValue(nextValue: string) {
    const trimmed = nextValue.trim();

    // Don't commit if duplicate or user is still "selecting an attribute" (trailing '.')
    if (
      trimmed.length === 0 ||
      trimmed.endsWith('.') ||
      values.includes(trimmed)
    ) {
      return false;
    }

    onSubmitValue(trimmed);
    setValue('');
    combobox.closeDropdown();
    return true;
  }

  function handleOptionSubmit(optionValue: string) {
    if (optionValue.startsWith(ADD_VALUE_PREFIX)) {
      commitValue(optionValue.slice(ADD_VALUE_PREFIX.length));
      return;
    }

    combobox.closeDropdown();

    const newValue =
      value.slice(0, partStart) + optionValue + value.slice(partEnd);

    if (isEditingLabel && !value.includes('.')) {
      commitValue(optionValue);
    } else if (isEditingLabel && value.includes('.')) {
      commitValue(newValue);
    } else {
      if (!commitValue(newValue)) {
        setValue(newValue);
      }
    }
  }

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={handleOptionSubmit}
      classNames={{
        dropdown: c('dropdown'),
      }}
    >
      <Combobox.Target>
        <TextInput
          id={id}
          value={value}
          rightSection={
            trimmedValue.length > 0 && (
              <Button
                size="xs"
                variant="default"
                disabled={!canConfirm || isDuplicateValue}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  commitValue(value);
                }}
              >
                Add
              </Button>
            )
          }
          rightSectionWidth={70}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              // If we can commit the whole value, do it immediately (single Enter),
              // otherwise let the combobox handle Enter (e.g. selecting a suggestion).
              if (commitValue(value)) {
                event.preventDefault();
              }
            }
          }}
          onKeyUp={(event) => {
            setCursorPosition(event.currentTarget.selectionStart);
          }}
          onChange={(event) => {
            setValue(event.currentTarget.value.trim());
            setCursorPosition(event.currentTarget.selectionStart);
            combobox.openDropdown();
            combobox.updateSelectedOptionIndex();
          }}
          onFocus={(event) => {
            setCursorPosition(event.currentTarget.selectionStart);
            combobox.openDropdown();
          }}
          onBlur={() => {
            setCursorPosition(null);
            combobox.closeDropdown();
          }}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          {filteredSuggestions.map((attribute) => (
            <Combobox.Option value={attribute} key={attribute}>
              {attribute}
            </Combobox.Option>
          ))}

          {isDuplicateValue && (
            <Combobox.Empty>"{trimmedValue}" already exists.</Combobox.Empty>
          )}

          {canShowAddOption && (
            <Combobox.Option
              value={`${ADD_VALUE_PREFIX}${trimmedValue}`}
              key={`${ADD_VALUE_PREFIX}${trimmedValue}`}
            >
              Add "{trimmedValue}"
            </Combobox.Option>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>

      {canConfirm && (
        <Text type="paragraph">
          Click <Kbd size="sm">Add</Kbd> or press <Kbd size="sm">↵</Kbd> to
          confirm current value.
          {isOnlyLabel && (
            <>
              Type <Kbd size="sm">.</Kbd> to select an attribute.
            </>
          )}
        </Text>
      )}
    </Combobox>
  );
}
